import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
    Plus,
    Webhook as WebhookIcon,
    Trash2,
    ExternalLink,
    CheckCircle,
    XCircle,
    Loader2,
    RefreshCw,
    Zap
} from 'lucide-react';
import { gymApi } from '@/lib/api';
import { Webhook } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const eventTypes = [
    { id: 'client_created', name: 'Cliente Creado', description: 'Se activa al registrar un nuevo cliente' },
    { id: 'membership_created', name: 'Membresía Creada', description: 'Se activa al asignar una membresía' },
    { id: 'checkin_success', name: 'Check-in Exitoso', description: 'Se activa al registrar una entrada válida' },
    { id: 'payment_received', name: 'Pago Recibido', description: 'Se activa al marcar una membresía como pagada' },
];

export default function WebhooksPage() {
    const { gym } = useAuth();
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { toast } = useToast();

    const [formData, setFormData] = useState({
        webhook_type: 'client_created',
        url: '',
        active: true,
    });

    useEffect(() => {
        Sentry.setTag("area", "revenue");
    }, []);

    useEffect(() => {
        fetchWebhooks();
    }, []);

    const fetchWebhooks = async () => {
        try {
            setIsLoading(true);
            const res = await gymApi.listWebhooks();
            if (res.success) {
                setWebhooks(res.webhooks);
            }
        } catch (error) {
            console.error('Failed to fetch webhooks:', error);
            Sentry.captureException(error, { tags: { operation: 'webhooks_fetch' } });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateWebhook = async () => {
        if (!formData.url.startsWith('http')) {
            toast({
                title: 'URL Inválida',
                description: 'La URL debe comenzar con http:// o https://',
                variant: 'destructive',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await gymApi.createWebhook();
            if (res.success) {
                await fetchWebhooks();
                setIsDialogOpen(false);
                setFormData({ webhook_type: 'client_created', url: '', active: true });
                toast({
                    title: 'Webhook configurado',
                    description: 'El evento ahora será enviado a n8n',
                });
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'webhooks_create' } });
            toast({
                title: 'Error',
                description: 'No se pudo guardar el webhook',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteWebhook = async (type: string) => {
        try {
            const res = await gymApi.deleteWebhook();
            if (res.success) {
                await fetchWebhooks();
                toast({
                    title: 'Webhook eliminado',
                    description: 'El evento ha sido desactivado',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'webhooks_delete' } });
            toast({
                title: 'Error',
                description: 'No se pudo eliminar el webhook',
                variant: 'destructive',
            });
        }
    };

    if (!gym) {
        return (
            <DashboardLayout title="Integraciones (Webhooks)" subtitle="Gestión global de integraciones">
                <Card className="border-warning/20 bg-warning/5">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-warning/10">
                                <Zap className="h-5 w-5 text-warning" />
                            </div>
                            <div>
                                <CardTitle>Selecciona un Gimnasio</CardTitle>
                                <CardDescription>
                                    Para gestionar webhooks, primero debes seleccionar un gimnasio desde el panel de Gimnasios.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                </Card>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout
            title="Integraciones (Webhooks)"
            subtitle={`Conecta ${gym.name} con n8n y otros servicios externos`}
            actions={
                <Button onClick={() => setIsDialogOpen(true)} variant="gradient">
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Integración
                </Button>
            }
        >
            <div className="grid gap-6">
                <Card className="border-primary/20 bg-primary/5">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Zap className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle>Automatiza con n8n</CardTitle>
                                <CardDescription>
                                    Configura webhooks para recibir notificaciones en tiempo real en tus flujos de n8n.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                </Card>

                <Card>
                    <CardContent className="pt-6">
                        {isLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : webhooks.length === 0 ? (
                            <div className="text-center py-10 space-y-3">
                                <WebhookIcon className="h-12 w-12 text-muted-foreground mx-auto opacity-20" />
                                <p className="text-muted-foreground">No hay integraciones activas</p>
                                <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                                    Configurar primera integración
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Evento</TableHead>
                                        <TableHead>URL de Destino (n8n)</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {webhooks.map((webhook) => (
                                        <TableRow key={webhook.webhook_type}>
                                            <TableCell className="font-medium">
                                                {eventTypes.find(e => e.id === webhook.webhook_type)?.name || webhook.webhook_type}
                                            </TableCell>
                                            <TableCell className="max-w-xs truncate font-mono text-xs">
                                                {webhook.url}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={webhook.active ? 'success' : 'muted'}>
                                                    {webhook.active ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => window.open(webhook.url, '_blank')}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive"
                                                        onClick={() => handleDeleteWebhook(webhook.webhook_type)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nueva Integración Webhook</DialogTitle>
                        <DialogDescription>
                            Selecciona un evento y proporciona la URL de tu flujo de n8n.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Evento a capturar</Label>
                            <Select
                                value={formData.webhook_type}
                                onValueChange={(val) => setFormData({ ...formData, webhook_type: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {eventTypes.map((event) => (
                                        <SelectItem key={event.id} value={event.id}>
                                            {event.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-muted-foreground mt-1">
                                {eventTypes.find(e => e.id === formData.webhook_type)?.description}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="url">URL del Webhook (n8n)</Label>
                            <Input
                                id="url"
                                placeholder="https://n8n.tu-dominio.com/webhook/..."
                                value={formData.url}
                                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                            Cancelar
                        </Button>
                        <Button
                            variant="gradient"
                            onClick={handleCreateWebhook}
                            disabled={isSubmitting || !formData.url}
                        >
                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Activar Integración
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
